/**
 * Fretboard voicings.
 *
 * Given the notes of a chord, search the first twelve frets in standard tuning for
 * shapes a hand can actually play, and rank them. "Playable" is not a vague idea
 * here — it is a set of stated constraints: a four-fret span, at most four
 * fingers, no muted string trapped between two sounding ones, and the root in the
 * bass wherever possible.
 *
 * Dense chords do not fit on six strings. Rather than silently dropping notes, the
 * search relaxes the requirement in a fixed order (the 5th first, then the widest
 * extension) and reports exactly which degree it had to leave out.
 */

import { midi, naturalPitchClass, pitchClass, transposeBy, type Pitch } from './pitch';
import { degreeLabel, type ChordQuality } from './chords';

/** Standard tuning, lowest string first. */
export const STANDARD_TUNING: Pitch[] = [
  { letter: 'E', alter: 0, octave: 2 },
  { letter: 'A', alter: 0, octave: 2 },
  { letter: 'D', alter: 0, octave: 3 },
  { letter: 'G', alter: 0, octave: 3 },
  { letter: 'B', alter: 0, octave: 3 },
  { letter: 'E', alter: 0, octave: 4 },
];

export const STRING_NAMES = ['E', 'A', 'D', 'G', 'B', 'e'];
export const MAX_FRET = 12;
/** Frets a hand can cover at once: min and max fretted notes within four frets. */
export const MAX_SPAN = 3;
export const MAX_FINGERS = 4;

export interface GuitarVoicing {
  /** One entry per string, low to high. null = muted, 0 = open. */
  frets: (number | null)[];
  /** Sounding notes, low to high, spelled to match the chord. Staff order. */
  pitches: Pitch[];
  /**
   * The note each string sounds, or null when it is muted. One entry per string,
   * low string first — the same order as `frets`.
   *
   * This is not `pitches` re-indexed, and treating it as such is a real bug that
   * shipped: `pitches` is sorted by pitch, and string order is not pitch order.
   * A B♭maj7 voicing that frets the low E at the 6th fret sounds B♭2 there while
   * the open A string below it sounds A2, so the two orders differ and the string
   * readout named every note one place out.
   */
  notesByString: (Pitch | null)[];
  /** Degrees the shape could not fit, e.g. ["5"]. */
  omittedDegrees: string[];
  /** Fret of the barre, if the shape needs one. */
  barre: number | null;
  fingers: number;
  span: number;
  lowestFret: number;
  score: number;
}

const openMidi = STANDARD_TUNING.map(midi);

/** Rebuild a spelled pitch for a sounding MIDI note, using the chord's spelling. */
function spellFromChord(midiNote: number, spellingByClass: Map<number, Pitch>): Pitch {
  const cls = ((midiNote % 12) + 12) % 12;
  const template = spellingByClass.get(cls);
  if (!template) {
    // Should not happen: every sounding note is a chord tone by construction.
    return { letter: 'C', alter: 0, octave: Math.floor(midiNote / 12) - 1 };
  }
  const octave = (midiNote - naturalPitchClass(template.letter) - template.alter) / 12 - 1;
  return { letter: template.letter, alter: template.alter, octave: Math.round(octave) };
}

interface SearchOptions {
  /** Pitch classes that must sound. */
  required: Set<number>;
  /** Pitch class preferred as the lowest sounding note. */
  rootClass: number;
  /** Every pitch class allowed to sound (chord tones, including any omitted). */
  allowed: Set<number>;
  spellingByClass: Map<number, Pitch>;
}

/** Enumerate playable shapes for one exact requirement set. */
function search(options: SearchOptions): GuitarVoicing[] {
  const { required, rootClass, allowed, spellingByClass } = options;

  // Per string, the frets that produce an allowed pitch class, plus "muted".
  const choices: (number | null)[][] = openMidi.map((open) => {
    const frets: (number | null)[] = [null];
    for (let fret = 0; fret <= MAX_FRET; fret++) {
      if (allowed.has(((open + fret) % 12 + 12) % 12)) frets.push(fret);
    }
    return frets;
  });

  const results: GuitarVoicing[] = [];
  const current: (number | null)[] = new Array(6).fill(null);

  const walk = (stringIndex: number, minFret: number, maxFret: number) => {
    if (stringIndex === 6) {
      const voicing = evaluate(current.slice(), required, rootClass, spellingByClass);
      if (voicing) results.push(voicing);
      return;
    }
    for (const fret of choices[stringIndex]) {
      let nextMin = minFret;
      let nextMax = maxFret;
      if (fret !== null && fret > 0) {
        nextMin = Math.min(minFret, fret);
        nextMax = Math.max(maxFret, fret);
        if (nextMax - nextMin > MAX_SPAN) continue; // hand cannot reach
      }
      current[stringIndex] = fret;
      walk(stringIndex + 1, nextMin, nextMax);
      current[stringIndex] = null;
    }
  };

  walk(0, Infinity, -Infinity);
  return results;
}

/** Turn a candidate shape into a scored voicing, or reject it. */
function evaluate(
  frets: (number | null)[],
  required: Set<number>,
  rootClass: number,
  spellingByClass: Map<number, Pitch>,
): GuitarVoicing | null {
  const sounding: { string: number; fret: number; midi: number }[] = [];
  frets.forEach((fret, i) => {
    if (fret !== null) sounding.push({ string: i, fret, midi: openMidi[i] + fret });
  });

  if (sounding.length < 3) return null;

  // Every required pitch class must actually sound.
  const classes = new Set(sounding.map((s) => ((s.midi % 12) + 12) % 12));
  for (const cls of required) if (!classes.has(cls)) return null;

  // No muted string trapped between two sounding strings — unplayable in practice.
  const first = sounding[0].string;
  const last = sounding[sounding.length - 1].string;
  for (let i = first; i <= last; i++) {
    if (frets[i] === null) return null;
  }

  const fretted = sounding.filter((s) => s.fret > 0);
  const lowestFret = fretted.length ? Math.min(...fretted.map((s) => s.fret)) : 0;
  const highestFret = fretted.length ? Math.max(...fretted.map((s) => s.fret)) : 0;
  const span = fretted.length ? highestFret - lowestFret : 0;
  if (span > MAX_SPAN) return null;

  // A barre means one finger genuinely lying across several strings. Two strings
  // that merely happen to share the lowest fret — D major's 2nd-fret notes on the
  // G and high E, with the B string above them — are played with separate fingers,
  // and drawing them as a bar would teach the wrong shape.
  // Two notes at the same fret several strings apart are held with one finger laid
  // flat — nobody splays two fingers to the same fret. So whenever the lowest fret
  // is used more than once, treat it as a bar for the purposes of what the hand
  // can physically do.
  const atLowest = fretted.filter((s) => s.fret === lowestFret);
  const impliedBarre = atLowest.length >= 2 && lowestFret > 0;

  if (impliedBarre) {
    // That flat finger covers everything between its ends, so nothing underneath
    // can ring open or be fretted lower. This is what rules out "103211" — an open
    // A string inside a first-fret bar cannot sound.
    const barred = atLowest.map((s) => s.string);
    for (let i = Math.min(...barred); i <= Math.max(...barred); i++) {
      const fret = frets[i];
      if (fret !== null && fret < lowestFret) return null;
    }
  }

  const fingers = impliedBarre
    ? 1 + fretted.filter((s) => s.fret > lowestFret).length
    : fretted.length;
  if (fingers > MAX_FINGERS) return null;

  // Whether to *draw* a bar is a separate question. D major's 2nd-fret notes on
  // the G and high E strings are played with separate fingers, and a bar across
  // them would teach the wrong shape.
  const adjacent =
    atLowest.length === 2 && Math.abs(atLowest[0].string - atLowest[1].string) === 1;
  const barre =
    impliedBarre && (atLowest.length >= 3 || (adjacent && fretted.length > MAX_FINGERS))
      ? lowestFret
      : null;

  const openCount = frets.filter((f) => f === 0).length;
  const bassClass = ((sounding[0].midi % 12) + 12) % 12;

  // --- ranking -------------------------------------------------------------
  // Tuned so the shapes a guitarist actually reaches for come out on top: open
  // position beats a barre up the neck even when the barre sounds more strings.
  let score = 0;
  score += sounding.length * 4; // fuller is good, but not at any cost
  score += openCount * 4; // open strings ring, and cost no fingers
  if (bassClass === rootClass) score += 22; // root in the bass
  else score -= 6;
  score -= span * 5;
  score -= fingers * 3;
  score -= lowestFret * 2.5; // low on the neck is much easier to reach
  // Barre difficulty is physical, so it follows the implied bar, not the decision
  // about whether to draw one.
  if (impliedBarre) score -= 4;
  // Holding a chord at the tenth fret while open strings ring is a stretch nobody
  // makes by choice.
  if (openCount > 0 && lowestFret >= 4) score -= 12;
  if (fretted.length === 0) score += 5; // fully open shapes are the easiest of all

  const notesByString: (Pitch | null)[] = frets.map(() => null);
  for (const s of sounding) notesByString[s.string] = spellFromChord(s.midi, spellingByClass);
  const pitches = sounding
    .map((s) => notesByString[s.string]!)
    .sort((a, b) => midi(a) - midi(b));

  return {
    frets,
    pitches,
    notesByString,
    omittedDegrees: [],
    barre,
    fingers,
    span,
    lowestFret,
    score,
  };
}

/**
 * Find ranked voicings for a chord. Relaxes the requirement set only as far as it
 * has to, and records what it gave up.
 */
export function findVoicings(
  chord: Pitch[],
  quality: ChordQuality,
  limit = 8,
): GuitarVoicing[] {
  if (!chord.length) return [];

  const rootClass = pitchClass(chord[0]);
  const spellingByClass = new Map<number, Pitch>();
  for (const p of chord) spellingByClass.set(pitchClass(p), p);
  const allowed = new Set(chord.map(pitchClass));

  // Degrees we are willing to drop, in order: the 5th carries the least
  // information, then the widest extension.
  const byDegree = chord.map((p, i) => ({
    pitch: p,
    label: quality.tones[i] ? degreeLabel(quality.tones[i]) : '?',
    diatonic: quality.tones[i]?.diatonic ?? 0,
  }));
  const droppable = byDegree
    .filter((d) => d.label === '5' || d.diatonic >= 7)
    .sort((a, b) => (a.label === '5' ? -1 : b.label === '5' ? 1 : b.diatonic - a.diatonic));

  for (let dropCount = 0; dropCount <= droppable.length; dropCount++) {
    const dropped = droppable.slice(0, dropCount);
    const droppedClasses = new Set(dropped.map((d) => pitchClass(d.pitch)));
    const required = new Set(
      chord.map(pitchClass).filter((cls) => !droppedClasses.has(cls)),
    );

    const found = search({ required, rootClass, allowed, spellingByClass });
    if (found.length) {
      return found
        .map((v) => {
          // Report what each shape actually leaves out: a voicing may happen to
          // include a note we were prepared to drop.
          const sounding = new Set(v.pitches.map(pitchClass));
          return {
            ...v,
            omittedDegrees: byDegree
              .filter((d) => !sounding.has(pitchClass(d.pitch)))
              .map((d) => d.label),
          };
        })
        .sort((a, b) => b.score - a.score)
        .filter(dedupe())
        .slice(0, limit);
    }
  }

  return [];
}

/** Drop shapes that produce an identical fret pattern. */
function dedupe() {
  const seen = new Set<string>();
  return (v: GuitarVoicing) => {
    const key = v.frets.map((f) => (f === null ? 'x' : f)).join('-');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  };
}

/** "x32010" style summary, low string first. */
export function fretString(voicing: GuitarVoicing): string {
  return voicing.frets.map((f) => (f === null ? 'x' : f.toString(36))).join('');
}

// ---------------------------------------------------- the written-guitar octave

/**
 * Guitar notation sounds an octave lower than it is written.
 *
 * This is not a quirk to be smoothed away — it is the convention, and a guitarist
 * reading a concert-pitch guitar staff would find every note an octave off from
 * where they expect it. So the dedicated guitar view writes the staff an octave
 * up and marks it with the octave-transposing treble clef, while the tablature,
 * the string readout and the audio all stay at the pitches the strings actually
 * produce. See `docs/THEORY-SOURCES.md` §3.5.
 *
 * The transform is deliberate and one-directional, which is why it lives here as
 * a named function rather than as `octave + 1` scattered through a component:
 * anywhere the two representations are compared, the comparison has to go
 * through this, not around it.
 */
export const GUITAR_WRITTEN_OFFSET_OCTAVES = 1;

/** Sounding pitch -> the pitch a guitarist reads on the staff. */
export function writtenForGuitar(sounding: Pitch): Pitch {
  // Seven letters and twelve semitones: an octave, with the spelling untouched.
  return transposeBy(sounding, 7 * GUITAR_WRITTEN_OFFSET_OCTAVES, 12 * GUITAR_WRITTEN_OFFSET_OCTAVES);
}

/** Written guitar pitch -> what the string actually sounds. */
export function soundingFromGuitarStaff(written: Pitch): Pitch {
  return transposeBy(written, -7 * GUITAR_WRITTEN_OFFSET_OCTAVES, -12 * GUITAR_WRITTEN_OFFSET_OCTAVES);
}
